import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { notify } from '@components/NotificationPlugin';
import {
  exportVideoWithSubtitles,
  type ExportVideoOptions,
} from '@utils/videoExport';

export type VideoExportJobPhase =
  | 'idle'
  | 'exporting'
  | 'completed'
  | 'cancelled'
  | 'error';

export type StartVideoExportJobOptions = Omit<
  ExportVideoOptions,
  'onProgress' | 'signal'
> & {
  originalName: string;
};

function outputFileName({
  originalName,
  preset,
  bitrate = 'recommended',
  fps = 'auto',
}: Pick<StartVideoExportJobOptions, 'originalName' | 'preset' | 'bitrate' | 'fps'>): string {
  const base = (originalName || 'video').replace(/\.[^./\\]+$/, '') || 'video';
  const fpsTag = fps !== 'auto' ? `-${fps}fps` : '';
  return `${base}-subtitled-${preset}-${bitrate}${fpsTag}.mp4`;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/**
 * Owns the active browser export outside the editor component tree. The Pinia
 * store survives modal closure and route changes, so its FFmpeg worker keeps
 * running while the user works elsewhere in the app. A page reload or closed
 * tab still terminates browser workers.
 */
export const useVideoExportStore = defineStore('videoExport', () => {
  const phase = ref<VideoExportJobPhase>('idle');
  const progressPct = ref(0);
  const fileName = ref('');
  const errorMessage = ref('');
  const isExporting = computed(() => phase.value === 'exporting');

  let abortController: AbortController | null = null;
  let dismissTimer: ReturnType<typeof setTimeout> | null = null;

  const clearDismissTimer = () => {
    if (dismissTimer !== null) {
      clearTimeout(dismissTimer);
      dismissTimer = null;
    }
  };

  const scheduleDismiss = (delayMs: number) => {
    clearDismissTimer();
    dismissTimer = setTimeout(() => {
      if (phase.value !== 'exporting') {
        phase.value = 'idle';
        progressPct.value = 0;
        errorMessage.value = '';
      }
      dismissTimer = null;
    }, delayMs);
  };

  const run = async (options: StartVideoExportJobOptions) => {
    try {
      const blob = await exportVideoWithSubtitles({
        ...options,
        onProgress: (percent: number) => {
          progressPct.value = Math.min(100, Math.max(0, percent));
        },
        signal: abortController?.signal,
      });
      progressPct.value = 100;
      phase.value = 'completed';
      downloadBlob(blob, fileName.value);
      void notify('success', 'Video exported successfully');
      scheduleDismiss(5000);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        phase.value = 'cancelled';
        errorMessage.value = '';
        void notify('info', 'Video export cancelled');
        scheduleDismiss(2500);
      } else {
        console.error('Video export failed:', error);
        phase.value = 'error';
        errorMessage.value = error instanceof Error ? error.message : String(error);
        void notify('danger', 'Export failed');
      }
    } finally {
      abortController = null;
    }
  };

  /**
   * Starts one global export and returns immediately. Only one memory-heavy
   * FFmpeg job may run at a time in this browser tab.
   */
  const start = (options: StartVideoExportJobOptions): boolean => {
    if (isExporting.value) return false;
    clearDismissTimer();
    abortController = new AbortController();
    fileName.value = outputFileName(options);
    errorMessage.value = '';
    progressPct.value = 0;
    phase.value = 'exporting';
    void run(options);
    return true;
  };

  const cancel = () => {
    if (isExporting.value) abortController?.abort();
  };

  const dismiss = () => {
    if (isExporting.value) return;
    clearDismissTimer();
    phase.value = 'idle';
    progressPct.value = 0;
    errorMessage.value = '';
  };

  return {
    phase,
    progressPct,
    fileName,
    errorMessage,
    isExporting,
    start,
    cancel,
    dismiss,
  };
});
